import { useState, useEffect } from "react";
import { useNavigate, Link, useSearchParams } from "react-router-dom";
import { Button } from "../components/ui/button";
import { Input } from "../components/ui/input";
import { Label } from "../components/ui/label";
import useAuthStore from "../store/authStore";
import { Eye, EyeOff, Loader2, Mail, Lock, User, Phone, Building2, MapPin, Factory, BarChart3, Gift, CheckCircle2 } from "lucide-react";
import Logo from "../components/Logo";
import api from "../services/api";
import { toast } from "sonner";

export default function B2BRegister() {
  const navigate = useNavigate();
  const [searchParams] = useSearchParams();
  const { loading } = useAuthStore();
  const [showPassword, setShowPassword] = useState(false);
  const [submitting, setSubmitting] = useState(false);
  const [referralCode, setReferralCode] = useState(() => searchParams.get("ref") || "");
  const [referralValid, setReferralValid] = useState(null);
  const [referralName, setReferralName] = useState("");

  const [form, setForm] = useState({
    name: "", email: "", password: "", phone: "",
    company_name: "", gst_number: "", business_address: "",
    industry_type: "", monthly_volume: "",
  });

  // Validate referral code
  useEffect(() => {
    const code = referralCode.trim();
    if (code.length < 4) { setReferralValid(null); setReferralName(""); return; }
    const timer = setTimeout(() => {
      api.get(`/referral/validate/${code}`)
        .then(({ data }) => { setReferralValid(data.valid); setReferralName(data.referrer_name || ""); })
        .catch(() => setReferralValid(false));
    }, 500);
    return () => clearTimeout(timer);
  }, [referralCode]);

  const handleSubmit = async (e) => {
    e.preventDefault();
    if (!form.name || !form.email || !form.password || !form.company_name || !form.business_address) {
      toast.error("Please fill all required fields");
      return;
    }
    setSubmitting(true);
    try {
      const { data } = await api.post("/auth/b2b-register", {
        ...form,
        referral_code: referralCode.trim() || undefined,
      });
      localStorage.setItem("token", data.token);
      useAuthStore.getState().setUser(data);
      toast.success("B2B account created!");
      navigate("/dashboard/b2b");
    } catch (err) {
      toast.error(err.response?.data?.detail || "Registration failed");
    }
    setSubmitting(false);
  };

  const industries = ["Manufacturer", "Distributor", "Retailer", "E-commerce", "Textile", "Auto Parts", "FMCG", "Pharma", "Electronics", "Other"];
  const volumes = ["1-50/month", "50-100/month", "100-500/month", "500-1000/month", "1000+/month"];

  return (
    <div className="min-h-screen bg-[#0B132B] flex items-center justify-center px-4 py-10" data-testid="b2b-register-page">
      <div className="absolute inset-0 bg-gradient-to-br from-[#FF6B00]/5 to-transparent pointer-events-none" />
      <div className="relative w-full max-w-lg">
        <div className="text-center mb-8">
          <div className="cursor-pointer inline-block" onClick={() => navigate("/b2b")}>
            <Logo size="lg" textColor="text-white" />
          </div>
          <div className="inline-flex items-center gap-2 bg-[#FF6B00]/20 text-[#FF6B00] px-4 py-1.5 rounded-full text-xs font-bold uppercase tracking-wider mt-4">
            <Building2 className="h-3.5 w-3.5" /> B2B Business Registration
          </div>
          <p className="text-slate-400 text-sm mt-3">Create your B2B Express account</p>
        </div>

        <form onSubmit={handleSubmit} className="bg-white rounded-2xl shadow-2xl p-7 space-y-4" data-testid="b2b-register-form">
          {/* Personal Info */}
          <p className="text-xs font-bold text-slate-400 uppercase tracking-wider">Personal Details</p>
          <div className="grid sm:grid-cols-2 gap-3">
            <div>
              <Label className="text-xs font-medium text-slate-600">Full Name *</Label>
              <div className="relative">
                <User className="absolute left-3 top-1/2 -translate-y-1/2 h-4 w-4 text-slate-400" />
                <Input placeholder="Your name" value={form.name} onChange={(e) => setForm({ ...form, name: e.target.value })} className="rounded-xl h-11 pl-9 text-sm" required data-testid="b2b-reg-name" />
              </div>
            </div>
            <div>
              <Label className="text-xs font-medium text-slate-600">Phone</Label>
              <div className="relative">
                <Phone className="absolute left-3 top-1/2 -translate-y-1/2 h-4 w-4 text-slate-400" />
                <Input placeholder="+91 9876543210" value={form.phone} onChange={(e) => setForm({ ...form, phone: e.target.value })} className="rounded-xl h-11 pl-9 text-sm" data-testid="b2b-reg-phone" />
              </div>
            </div>
          </div>
          <div className="grid sm:grid-cols-2 gap-3">
            <div>
              <Label className="text-xs font-medium text-slate-600">Email *</Label>
              <div className="relative">
                <Mail className="absolute left-3 top-1/2 -translate-y-1/2 h-4 w-4 text-slate-400" />
                <Input type="email" placeholder="you@company.com" value={form.email} onChange={(e) => setForm({ ...form, email: e.target.value })} className="rounded-xl h-11 pl-9 text-sm" required data-testid="b2b-reg-email" />
              </div>
            </div>
            <div>
              <Label className="text-xs font-medium text-slate-600">Password *</Label>
              <div className="relative">
                <Lock className="absolute left-3 top-1/2 -translate-y-1/2 h-4 w-4 text-slate-400" />
                <Input type={showPassword ? "text" : "password"} placeholder="Create password" value={form.password} onChange={(e) => setForm({ ...form, password: e.target.value })} className="rounded-xl h-11 pl-9 pr-9 text-sm" required data-testid="b2b-reg-password" />
                <button type="button" onClick={() => setShowPassword(!showPassword)} className="absolute right-3 top-1/2 -translate-y-1/2 text-slate-400">
                  {showPassword ? <EyeOff className="h-4 w-4" /> : <Eye className="h-4 w-4" />}
                </button>
              </div>
            </div>
          </div>

          {/* Company Info */}
          <div className="border-t border-slate-100 pt-4">
            <p className="text-xs font-bold text-slate-400 uppercase tracking-wider mb-3">Company Details</p>
          </div>
          <div className="grid sm:grid-cols-2 gap-3">
            <div>
              <Label className="text-xs font-medium text-slate-600">Company Name *</Label>
              <div className="relative">
                <Building2 className="absolute left-3 top-1/2 -translate-y-1/2 h-4 w-4 text-slate-400" />
                <Input placeholder="Company name" value={form.company_name} onChange={(e) => setForm({ ...form, company_name: e.target.value })} className="rounded-xl h-11 pl-9 text-sm" required data-testid="b2b-reg-company" />
              </div>
            </div>
            <div>
              <Label className="text-xs font-medium text-slate-600">GST Number</Label>
              <Input placeholder="e.g., 22AAAAA0000A1Z5" value={form.gst_number} onChange={(e) => setForm({ ...form, gst_number: e.target.value })} className="rounded-xl h-11 text-sm" data-testid="b2b-reg-gst" />
            </div>
          </div>
          <div>
            <Label className="text-xs font-medium text-slate-600">Business Address *</Label>
            <div className="relative">
              <MapPin className="absolute left-3 top-3 h-4 w-4 text-slate-400" />
              <Input placeholder="Full business address" value={form.business_address} onChange={(e) => setForm({ ...form, business_address: e.target.value })} className="rounded-xl h-11 pl-9 text-sm" required data-testid="b2b-reg-address" />
            </div>
          </div>
          <div className="grid sm:grid-cols-2 gap-3">
            <div>
              <Label className="text-xs font-medium text-slate-600">Industry Type</Label>
              <select value={form.industry_type} onChange={(e) => setForm({ ...form, industry_type: e.target.value })} className="w-full rounded-xl h-11 border border-slate-200 px-3 text-sm focus:border-[#FF6B00] focus:outline-none focus:ring-2 focus:ring-[#FF6B00]/20" data-testid="b2b-reg-industry">
                <option value="">Select industry</option>
                {industries.map((i) => <option key={i} value={i}>{i}</option>)}
              </select>
            </div>
            <div>
              <Label className="text-xs font-medium text-slate-600">Expected Monthly Volume</Label>
              <select value={form.monthly_volume} onChange={(e) => setForm({ ...form, monthly_volume: e.target.value })} className="w-full rounded-xl h-11 border border-slate-200 px-3 text-sm focus:border-[#FF6B00] focus:outline-none focus:ring-2 focus:ring-[#FF6B00]/20" data-testid="b2b-reg-volume">
                <option value="">Select volume</option>
                {volumes.map((v) => <option key={v} value={v}>{v}</option>)}
              </select>
            </div>
          </div>

          {/* Referral Code */}
          <div>
            <Label className="text-xs font-medium text-slate-600">Referral Code <span className="text-slate-400">(optional)</span></Label>
            <div className="relative">
              <Gift className="absolute left-3 top-1/2 -translate-y-1/2 h-4 w-4 text-slate-400" />
              <Input
                placeholder="Enter referral code"
                value={referralCode} onChange={(e) => setReferralCode(e.target.value.toUpperCase())}
                className={`rounded-xl h-11 pl-9 pr-9 text-sm ${referralValid === true ? "border-green-400 ring-2 ring-green-100" : referralValid === false ? "border-red-400 ring-2 ring-red-100" : ""}`}
                data-testid="b2b-reg-referral"
              />
              {referralValid === true && <CheckCircle2 className="absolute right-3 top-1/2 -translate-y-1/2 h-4 w-4 text-green-500" />}
            </div>
            {referralValid === true && referralName && <p className="text-xs text-green-600 font-medium mt-1">Referred by {referralName} — INR 30 off your first order!</p>}
          </div>

          <Button type="submit" disabled={submitting} className="w-full bg-[#FF6B00] hover:bg-[#E65A00] text-white rounded-xl h-12 text-base font-semibold" data-testid="b2b-reg-submit">
            {submitting ? <Loader2 className="h-5 w-5 animate-spin" /> : "Create B2B Account"}
          </Button>

          <div className="text-center space-y-2 pt-1">
            <p className="text-sm text-slate-500">
              Already have a B2B account?{" "}
              <Link to="/b2b/login" className="text-[#FF6B00] font-semibold hover:underline">Login</Link>
            </p>
            <p className="text-xs text-slate-400">
              Already a B2C customer?{" "}
              <Link to="/login" className="text-slate-600 font-medium hover:underline">Login</Link>
              {" "}and upgrade to B2B from your dashboard
            </p>
          </div>
        </form>
      </div>
    </div>
  );
}
